import { useQuery } from '@tanstack/react-query'
import { customReportsApi, useNamedQuery } from '~/api/reports'
import { useGetSubdivisionsByAlpha2CountryCode } from '~/api/countrySubdivisions'
import { DashboardQueryItem, DashboardQueryOrderBy, DashboardQueryParams, Fields } from '~/dashboards/common'
import { NamedQueryFilter, ReportColumnConfig } from '~/custom_reports/types'
import { Datum } from '~/design_system'
import fetch from '../common/fetch'

export const getParsedValue = (value: string, format: 'number' | 'currency'): number => {
  if (value === '') return 0
  return format === 'number' ? parseInt(value.replace(/,/g, '') ?? '0', 10) : parseFloat(value.replace(/,/g, ''))
}

const transformItem = (item: DashboardQueryItem, fields: Fields, valueFormat: 'number' | 'currency') => {
  const value = item[fields.value]

  return {
    id: item[fields.id],
    value: typeof value === 'string' ? getParsedValue(value, valueFormat) : value
  }
}

type useTabularDataProps = {
  id: string
  fields: Fields
  filters: NamedQueryFilter[]
  orderBy: DashboardQueryOrderBy[]
  transform: (
    item: DashboardQueryItem,
    fields: Fields,
    valueFormat: 'number' | 'currency'
  ) => { id: string | number; value: number }
  valueFormat: 'number' | 'currency'
  limit: number
  includeItems: (item: DashboardQueryItem) => DashboardQueryItem
}

/** Custom hook to transform the response with (key, value) pairs */
export const useTabularData = ({
  id,
  fields,
  filters = [],
  orderBy = [],
  transform = transformItem,
  valueFormat = 'number',
  limit = 10,
  includeItems = item => item
}: useTabularDataProps) => {
  const {
    data: { data } = {},
    isLoading,
    isError,
    isFetching
  } = useNamedQuery({
    queryName: id,
    namedQueryParams: { fields: Object.values(fields), filters, orderBy },
    page: 0,
    pageSize: limit,
    urlParams: {
      format: 'json',
      col_format: 'api'
    }
  })

  const transformedData = data
    ? data
        .filter((item: DashboardQueryItem) => item[fields.id] !== '' && item[fields.id] !== 'N/A')
        .filter(includeItems)
        .map((item: DashboardQueryItem) => transform(item, fields, valueFormat))
    : data

  return { data: transformedData, isLoading, isError, isFetching }
}

type useRowOnlyDataProps = {
  id: string
  field: string
  filters: NamedQueryFilter[]
  valueFormat?: 'number' | 'currency'
  limit?: number
}

/** Custom hook to transform the response with list of values */
export const useRowOnlyData = ({ id, field, filters = [], valueFormat = 'number', limit = 1 }: useRowOnlyDataProps) => {
  const {
    data: { data } = {},
    isLoading,
    isError,
    isFetching
  } = useNamedQuery({
    queryName: id,
    namedQueryParams: { fields: [field], filters },
    page: 0,
    pageSize: limit,
    urlParams: {}
  })

  const transformedData = data
    ? data[0]
        .filter((item: string) => item !== '' && item !== 'N/A')
        .map((value: string | number) => ({
          id: field,
          value: typeof value === 'string' ? getParsedValue(value, valueFormat) : value
        }))
    : data

  return { data: transformedData, isLoading, isError, isFetching }
}

export const useDashboardPermissions = () => {
  const { data } = useQuery({
    queryKey: ['dashboards', 'permissions'],
    queryFn: () => fetch.get('/api/dashboard_permissions/')
  })

  return data
}

const transform2dData = (
  data: DashboardQueryItem[],
  columns: ReportColumnConfig[],
  valueFormat: 'number' | 'currency'
) => {
  const columnNames = columns?.map(col => col.name)
  return data
    ?.filter(item => !['', 'N/A'].includes(item[columnNames[0]]?.toString() ?? ''))
    .map(item => {
      const value = item[columnNames[1]]
      return {
        id: item[columnNames[0]],
        value: typeof value === 'string' ? getParsedValue(value, valueFormat) : value
      }
    })
}

type TransformerFieldDef = { id: string; label?: string }

/**
 * Creates a data transformer for pie charts that calculates the difference between two numeric fields.
 *
 * Transforms data by:
 * 1. Taking a value to subtract from a total
 * 2. Calculating the difference (total - value)
 * 3. Returning both the original value and calculated difference for chart visualization
 *
 * @param config - Configuration object with field definitions
 * @param config.toDiffFieldDef - Field definition for the value to subtract (e.g., "interviewed")
 * @param config.totalFieldDef - Field definition for the total value (e.g., "total applied")
 * @param config.diffFieldDef - Field definition for the calculated difference (e.g., "not interviewed")
 * @returns A transformer function that processes dashboard data
 */
export const transformToFieldAndDifferenceOfTotal =
  ({
    toDiffFieldDef,
    totalFieldDef,
    diffFieldDef
  }: {
    toDiffFieldDef: TransformerFieldDef
    totalFieldDef: TransformerFieldDef
    diffFieldDef: TransformerFieldDef
  }) =>
  (
    data: { [key: string]: string | number | null }[],
    columns: ReportColumnConfig[],
    valueFormat: 'number' | 'currency'
  ): Datum[] => {
    // if none of the params are provided, return empty array
    if (!columns || !data || !data[0] || !toDiffFieldDef || !totalFieldDef || !diffFieldDef) return []
    let toDiffValue = data[0][toDiffFieldDef.id]
    let totalValue = data[0][totalFieldDef.id]
    // if any of the values are null, return empty array
    if (!toDiffValue || !totalValue) return []
    toDiffValue = (typeof toDiffValue === 'string' ? getParsedValue(toDiffValue, valueFormat) : toDiffValue) ?? 0
    totalValue = (typeof totalValue === 'string' ? getParsedValue(totalValue, valueFormat) : totalValue) ?? 0
    const diffValue = totalValue - toDiffValue

    return [
      {
        id: toDiffFieldDef.id, // matching id with the column name
        label: toDiffFieldDef?.label, // optional label to show in the Summary/Legend
        value: data[0][toDiffFieldDef.id] ?? 0
      },
      {
        id: diffFieldDef.id, // matching id with the column name
        label: diffFieldDef?.label, // optional label to show in the Summary/Legend
        value: diffValue
      }
    ]
  }

export const transformSingleRowResultToLabeledValues =
  (fields: { id: string; label: string }[]) =>
  (
    data: { [key: string]: string | number | null }[],
    columns: ReportColumnConfig[],
    valueFormat: 'number' | 'currency'
  ): Datum[] => {
    return columns
      ?.filter(column => data[0][column.id] != null)
      .map(column => {
        const value = data[0][column.id] ?? ''

        return {
          id: column.id,
          label: fields?.find(field => field.id === column.id)?.label,
          value: typeof value === 'string' ? getParsedValue(value, valueFormat) : value
        }
      })
  }

type useDashboardReportProps = {
  id: string
  queryName: string
  dashboardQueryParams?: DashboardQueryParams
  transformResult?: (
    data: DashboardQueryItem[],
    columns: ReportColumnConfig[],
    valueFormat: 'number' | 'currency'
  ) => { id: string | number; value: string | number }[]
  valueFormat?: 'number' | 'currency'
  limit?: number
  useTransform?: boolean
}

export const useDashboardReport = (
  {
    id,
    queryName,
    dashboardQueryParams,
    transformResult = transform2dData,
    valueFormat = 'number',
    limit = 10,
    useTransform = true
  }: useDashboardReportProps,
  isReady = true
) => {
  const {
    data: { data, columns } = {},
    isLoading,
    isError,
    isFetching,
    status,
    error
  } = useQuery({
    queryKey: [id, queryName, dashboardQueryParams],
    queryFn: () =>
      customReportsApi.runDashboardQuery(queryName, dashboardQueryParams, 0, limit, {
        format: 'json',
        col_format: 'api'
      }),
    enabled: isReady,
    keepPreviousData: true
  })
  // @ts-ignore
  const errorCode = status === 'error' ? error?.status : null
  const transformedData = useTransform ? transformResult(data, columns, valueFormat) : data
  return { data: transformedData, isLoading, isError, isFetching, errorCode }
}

type BulkDashboardQueryParams = {
  [key: string]: DashboardQueryParams
}

export const updateDashboardQueryParams = (bulkDashboardQueryParams: BulkDashboardQueryParams) => {
  customReportsApi.updateDashboardQueryParams(bulkDashboardQueryParams)
}

const COUNTRY_CODE = 'US'

type useChoroplethDataProps = {
  id: string
  queryName: string
  dashboardQueryParams: DashboardQueryParams
  format?: 'number' | 'currency'
  countryCode?: string
}

export const useChoroplethDataV2 = ({
  id,
  queryName,
  dashboardQueryParams,
  format = 'number',
  countryCode = COUNTRY_CODE
}: useChoroplethDataProps) => {
  const { status: stateCodesStatus, data: stateCodes } = useGetSubdivisionsByAlpha2CountryCode(countryCode)
  const transformResult = (data: DashboardQueryItem[], columns: ReportColumnConfig[]) => {
    const columnNames = columns?.map(col => col.name)

    return data
      ?.filter(item => {
        const itemColumn = item[columnNames[0]]
        return (
          itemColumn &&
          itemColumn.toString().trim() !== '' &&
          stateCodes?.find(stateCode => stateCode.subdivision_code === itemColumn)
        )
      })
      .map(item => {
        const value = item[columnNames[1]]
        return {
          id: item[columnNames[0]],
          value: typeof value === 'string' ? getParsedValue(value, format) : value
        }
      })
  }
  return useDashboardReport(
    { id, queryName, dashboardQueryParams, transformResult, valueFormat: format, limit: 1000 },
    stateCodesStatus === 'success'
  )
}
